import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../App'
import { Button } from '../ui/Button'

const NAV_LINKS = [
  { label: 'Product',  href: '/#features' },
  { label: 'Pricing',  href: '/pricing' },
  { label: 'Security', href: '/security' },
  { label: 'Legal',    href: '/legal' },
]

function ConnectPopover({ onClose }) {
  const ref = useRef(null)

  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [onClose])

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-52 bg-white rounded-lg shadow-lg border border-ink-300 p-4 z-50"
      role="dialog"
      aria-label="Connect with us"
    >
      <p className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-3">Connect</p>
      <div className="flex flex-col gap-2">
        <a
          href="https://linkedin.com/company/solagrant"
          target="_blank"
          rel="noreferrer"
          className="text-sm text-ink-700 hover:text-ink-900 hover:bg-ink-100 rounded-md px-2 py-1.5 transition-colors"
        >
          LinkedIn
        </a>
        <a
          href="https://twitter.com/solagrant"
          target="_blank"
          rel="noreferrer"
          className="text-sm text-ink-700 hover:text-ink-900 hover:bg-ink-100 rounded-md px-2 py-1.5 transition-colors"
        >
          X / Twitter
        </a>
        <a
          href="https://youtube.com/@solagrant"
          target="_blank"
          rel="noreferrer"
          className="text-sm text-ink-700 hover:text-ink-900 hover:bg-ink-100 rounded-md px-2 py-1.5 transition-colors"
        >
          YouTube
        </a>
      </div>
    </div>
  )
}

function MobileDrawer({ open, onClose }) {
  const drawerRef = useRef(null)
  const { user } = useAuth()

  // Lock body scroll and focus close button when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      drawerRef.current?.querySelector('[data-close]')?.focus()
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  // Focus trap + Escape
  useEffect(() => {
    if (!open) return
    function handleKey(e) {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key === 'Tab') {
        const focusable = drawerRef.current?.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        )
        if (!focusable?.length) return
        const first = focusable[0]
        const last  = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-ink-900/40 md:hidden transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        id="mobile-drawer"
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className={`fixed inset-y-0 right-0 z-50 w-72 bg-white md:hidden flex flex-col
                    transform transition-transform duration-200 ease-out
                    ${open ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pb-4 border-b border-ink-300">
          <span className="font-semibold text-ink-900">SolaGrant</span>
          <button
            data-close
            onClick={onClose}
            className="min-h-11 min-w-11 flex items-center justify-center rounded-lg text-ink-500 hover:text-ink-900 hover:bg-ink-100"
            aria-label="Close navigation menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Nav links */}
        <nav aria-label="Mobile navigation" className="flex-1 px-5 py-4 flex flex-col gap-1 overflow-y-auto">
          {NAV_LINKS.map(link => (
            <Link
              key={link.label}
              to={link.href}
              onClick={onClose}
              className="flex items-center h-14 text-base font-medium text-ink-700 hover:text-ink-900 hover:bg-ink-100 rounded-lg px-3 transition-colors"
            >
              {link.label}
            </Link>
          ))}
          <a
            href="https://linkedin.com/company/solagrant"
            target="_blank"
            rel="noreferrer"
            className="flex items-center h-14 text-base font-medium text-ink-700 hover:text-ink-900 hover:bg-ink-100 rounded-lg px-3 transition-colors"
          >
            Connect
          </a>
        </nav>

        {/* Auth CTAs */}
        <div className="px-5 py-4 border-t border-ink-300 flex flex-col gap-3">
          {user ? (
            <Button as={Link} to="/app/dashboard" size="lg" className="w-full" onClick={onClose}>
              Go to dashboard
            </Button>
          ) : (
            <>
              <Button as={Link} to="/login" variant="secondary" size="lg" className="w-full" onClick={onClose}>
                Log in
              </Button>
              <Button as={Link} to="/signup" size="lg" className="w-full" onClick={onClose}>
                Start free
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default function MarketingNav() {
  const [drawerOpen,  setDrawerOpen]  = useState(false)
  const [connectOpen, setConnectOpen] = useState(false)
  const { user } = useAuth()
  const location = useLocation()

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false) }, [location.pathname])

  return (
    <>
      <header className="sticky top-0 z-40 h-14 md:h-16 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 border-b border-ink-300/50">
        <div className="mx-auto max-w-container px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between gap-4">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 flex-shrink-0 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
            aria-label="SolaGrant home"
          >
            <div className="w-7 h-7 rounded-md bg-brand-600 flex items-center justify-center" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 2L14 13H2L8 2Z" fill="white"/>
              </svg>
            </div>
            <span className="font-semibold text-ink-900 text-base">SolaGrant</span>
          </Link>

          {/* Desktop nav */}
          <nav aria-label="Primary" className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map(link => {
              const isCurrent = link.href === location.pathname
              return (
                <Link
                  key={link.label}
                  to={link.href}
                  aria-current={isCurrent ? 'page' : undefined}
                  className={`px-3 h-9 flex items-center text-sm font-medium rounded-lg transition-colors ${
                    isCurrent
                      ? 'text-ink-900 bg-ink-100'
                      : 'text-ink-500 hover:text-ink-900 hover:bg-ink-100'
                  }`}
                >
                  {link.label}
                </Link>
              )
            })}
            <div className="relative">
              <button
                onClick={() => setConnectOpen(s => !s)}
                className="px-3 h-9 flex items-center text-sm font-medium text-ink-500 hover:text-ink-900 hover:bg-ink-100 rounded-lg transition-colors"
                aria-expanded={connectOpen}
                aria-haspopup="dialog"
              >
                Connect
              </button>
              {connectOpen && <ConnectPopover onClose={() => setConnectOpen(false)} />}
            </div>
          </nav>

          {/* Desktop right CTAs */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            {user ? (
              <Button as={Link} to="/app/dashboard" size="md">Dashboard</Button>
            ) : (
              <>
                <Button as={Link} to="/login" variant="ghost" size="md">Log in</Button>
                <Button as={Link} to="/signup" size="md">Start free</Button>
              </>
            )}
          </div>

          {/* Mobile: visible Start free + hamburger */}
          <div className="flex md:hidden items-center gap-2">
            {!user && (
              <Button as={Link} to="/signup" size="md" className="text-sm h-9 px-4">Start free</Button>
            )}
            <button
              onClick={() => setDrawerOpen(true)}
              className="min-h-11 min-w-11 flex items-center justify-center rounded-lg text-ink-700 hover:bg-ink-100"
              aria-label="Open navigation menu"
              aria-expanded={drawerOpen}
              aria-controls="mobile-drawer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
          </div>

        </div>
      </header>

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  )
}
